import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, CalendarPlus, ArrowLeft } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { SimpleLocationsSection } from '@/components/sections/simple-locations-section'
import { TableOfContents } from '@/components/sections/table-of-contents'

const tocItems = [
  { id: 'introduction-section', title: 'Women\'s Health & Long-Term Care' },
  { id: 'who-this-is-for', title: 'Who This Is For' },
  { id: 'common-reasons', title: 'Common Reasons People Seek Long-Term Support' },
  { 
    id: 'how-acupuncture-helps', 
    title: 'How Acupuncture & Chinese Medicine May Help Over Time',
    children: [
      { id: 'life-stages-transitions', title: 'Life Stages & Transitions' },
      { id: 'how-ongoing-care-structured', title: 'How Ongoing Care Can Be Structured' },
    ]
  },
  { id: 'what-to-expect', title: 'What to Expect' },
]

export default function WomensHealthLongTermCarePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const showBackLink = location.state?.fromParent === true
  const showHomepageBackLink = location.state?.fromHomepage === true
  const showAppointmentsBackLink = location.state?.fromAppointmentsPage === true

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Ongoing Women's Health & Long-Term Care | Raintree Chinese Medicine</title>
        <meta name="description" content="Ongoing women's health and long-term care at Raintree Chinese Medicine. Continuity of care through different life stages." />
        <meta property="og:title" content="Ongoing Women's Health & Long-Term Care | Raintree Chinese Medicine" />
        <meta property="og:description" content="Ongoing women's health and long-term care at Raintree Chinese Medicine. Continuity of care through different life stages." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/what-we-help-with/womens-health-long-term-care" />
      </Helmet>
      <h1 className="sr-only">Ongoing Women's Health & Long-Term Care</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0 relative">
        {showHomepageBackLink && (
          <button
            onClick={() => navigate('/#what-we-help-with-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        {showAppointmentsBackLink && !showHomepageBackLink && (
          <button
            onClick={() => navigate('/appointments#areas-of-focus-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to Appointments and Fees
          </button>
        )}
        {showBackLink && !showHomepageBackLink && !showAppointmentsBackLink && (
          <button
            onClick={() => navigate('/what-we-help-with')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Ongoing Women's Health & Long-Term Care
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Continuity of care through different life stages.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Appointment Types & Fees
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('longterm_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book an Initial Consultation & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('longterm_hero_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Book a free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Ongoing Women's Health & Long-Term Care
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                  Some people come for a specific concern and stay because they value having a steady practitioner over time.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Appointment Types & Fees
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('longterm_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book an Initial Consultation & Treatment
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('longterm_hero_free_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book a free 15-minute phone consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="introduction-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Women's Health & Long-Term Care
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Women's health is not just one phase – it's a thread that runs through many stages of life.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                This page is for people who:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Want ongoing support across different chapters – not just a short burst of care</li>
                <li>Value having the same practitioner who knows their story over time</li>
                <li>Have multiple overlapping concerns (hormones, stress, digestion, fertility, pregnancy, perimenopause)</li>
                <li>Want a place to come back to when things shift, change or flare again</li>
              </ul>
              <p className="text-gray-600 text-left">
                Renee offers acupuncture and Chinese herbal medicine for women's health with a long-term, relationship-based approach, alongside your care from GPs, specialists and other practitioners.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section id="who-this-is-for" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Who This Is For
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Long-term women's health care may be a good fit if you:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Have health patterns that come and go over months or years, rather than one simple issue</li>
                <li>Are moving through big transitions – work, family, grief, parenting, menopause – and feel them in your body</li>
                <li>Have had some benefit from acupuncture or Chinese medicine before and want to build on that</li>
                <li>Don't want to start from scratch with a new practitioner every time things change</li>
                <li>Want someone who remembers your history and can help you connect the dots</li>
              </ul>
              <p className="text-gray-600 text-left">
                Some people come in for a specific issue and stay because they find it helpful to have a regular, familiar place to check in, reassess and reset as life evolves.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Reasons People Seek Long-Term Support Section */}
      <section id="common-reasons" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Common Reasons People Seek Long-Term Support
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Everyone's story is different, but some common patterns include:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Cycles that have been difficult for years – painful, heavy or irregular periods, or strong PMS/PMDD</li>
                <li>Ongoing hormonal conditions such as suspected or diagnosed PCOS or endometriosis symptoms</li>
                <li>Fertility journeys that unfold over several years, with time spent trying to conceive, undergoing treatment or taking breaks</li>
                <li>Multiple pregnancies and postpartum recoveries, with shifting physical and emotional needs each time</li>
                <li>Perimenopausal changes layered on top of long-standing patterns of stress, poor sleep or digestive issues</li>
                <li>The cumulative impact of caring responsibilities, work, ageing parents and changes in relationships</li>
              </ul>
              <p className="text-gray-600 text-left">
                This kind of care is less about "fixing one problem quickly" and more about having support as your body and life move through different chapters.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Acupuncture & Chinese Medicine May Help Over Time Section */}
      <section id="how-acupuncture-helps" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              How Acupuncture & Chinese Medicine May Help Over Time
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                In long-term women's health care, acupuncture and Chinese medicine are used to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Keep an ongoing view of your cycles, sleep, digestion, mood and energy – not just one symptom</li>
                <li>Adjust treatment as new life events or health changes arise</li>
                <li>Support your nervous system through periods of stress, illness, transition and recovery</li>
                <li>Offer a regular space to pause, reflect and feel looked after</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Because the relationship is ongoing, there is room to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Notice patterns across years, not just weeks</li>
                <li>Work in "seasons" – periods of more frequent treatment, followed by lighter maintenance</li>
                <li>Revisit topics as needed without retelling your whole history each time</li>
              </ul>
              <p className="text-gray-600 text-left">
                Treatment is always intended to sit <strong>alongside</strong>, not replace, the care you receive from your GP, gynaecologist, endocrinologist, psychologist or other health providers.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Life Stages & Transitions Section */}
      <section id="life-stages-transitions" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Life Stages & Transitions
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Long-term care often covers several of the following phases.
              </p>
            </div>
            
            {/* Early Cycles & Reproductive Years */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Early Cycles & Reproductive Years
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  This may include support for:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Getting to know your cycle and what is normal for you</li>
                  <li>Menstrual pain, heavy or irregular periods, PMS/PMDD</li>
                  <li>Hormonal concerns that show up in skin, mood or energy</li>
                  <li>The impact of stress, study, work and relationships on your health</li>
                </ul>
                <p className="text-gray-600 text-left">
                  For many, this is the stage where patterns first show themselves – and where early support can be very helpful.
                </p>
              </div>
            </div>

            {/* Fertility, Pregnancy & Postpartum */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Fertility, Pregnancy & Postpartum
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Long-term care may move through:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Preconception and fertility support (with or without IVF)</li>
                  <li>Support during pregnancy, including comfort, sleep and nervous-system regulation</li>
                  <li>Recovery after birth, rebuilding energy and adjusting to life with a baby or young children</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Some people have several pregnancies over the years. Having one practitioner who knows your fertility and pregnancy history can make it easier to plan care that fits each new chapter.
                </p>
              </div>
            </div>

            {/* Perimenopause & Beyond */}
            <div>
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Perimenopause & Beyond
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Later on, long-term care often includes support for:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Perimenopausal changes in cycles, sleep, mood and temperature regulation</li>
                  <li>The emotional side of this transition – identity, work, family dynamics and ageing</li>
                  <li>The body's changing needs for rest, nutrition and movement</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Here, sessions can help you keep track of what is changing, support your nervous system and digestion, and offer steadiness while you decide on next steps with your medical team (such as HRT or other treatments, if appropriate).
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Ongoing Care Can Be Structured Section */}
      <section id="how-ongoing-care-structured" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              How Ongoing Care Can Be Structured
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Long-term support does not mean weekly appointments forever. It might look like:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>A more frequent block of treatment when things are acute or changing (for example, during IVF cycles, postpartum, or when perimenopausal symptoms flare)</li>
                <li>Gradually spacing appointments out as things stabilise (fortnightly, monthly, or every few months)</li>
                <li>Periodic check-ins at times of transition or increased stress</li>
                <li>Coming back after a break when a new issue arises or an old pattern reappears</li>
              </ul>
              <p className="text-gray-600 text-left">
                You and Renee can adjust the rhythm of care according to what is happening in your life, what is clinically appropriate, and what is realistic for you.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section id="what-to-expect" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              What to Expect in a Long-Term Care Appointment
            </h2>
            
            {/* Your First Visit */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Your First Visit
                </h3>
                <p className="text-gray-600 mb-4 text-left font-medium">
                  Your initial consultation and treatment is a longer appointment to build a clear picture of your health over time.
                </p>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Together you will:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Talk through your health history across different life stages – periods, pregnancies, stress, illnesses and treatments</li>
                  <li>Map your current concerns: hormones, cycles, sleep, digestion, pain, mood and energy</li>
                  <li>Discuss what other care you are receiving (GP, specialists, counselling, allied health)</li>
                  <li>Clarify what you are hoping for – whether that is symptom change, steadier energy, or having regular support through a transition</li>
                  <li>Begin acupuncture treatment, and, where appropriate, discuss Chinese herbal medicine and initial, manageable self-care steps</li>
                </ul>
                <p className="text-gray-600 text-left">
                  The aim is to understand your longer story, not just this week's symptoms.
                </p>
              </div>
            </div>

            {/* Ongoing Appointments */}
            <div>
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Ongoing Appointments
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Over time, follow-up appointments focus on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Checking in on how you have been since your last visit – physically and emotionally</li>
                  <li>Adjusting treatment in line with changes in your health, cycle, fertility, pregnancy, work or family life</li>
                  <li>Reviewing whether the current rhythm of appointments still makes sense</li>
                  <li>Planning ahead for upcoming transitions (such as IVF, surgery, travel, busy periods or perimenopausal changes)</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Because the relationship is ongoing, there is room to refine, pause, restart and adapt as needed.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Emotional Support & Therapeutic Relationship Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Emotional Support & Therapeutic Relationship
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                A long-term therapeutic relationship can offer:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>A familiar, calm space where you don't have to start from the beginning each time</li>
                <li>Someone who remembers your history and can help you see patterns and progress</li>
                <li>A place to talk through changes and decisions in a grounded, non-judgemental way</li>
                <li>Regularly scheduled times that act as a kind of anchor during busy or turbulent seasons</li>
              </ul>
              <p className="text-gray-600 text-left">
                Acupuncture and Chinese medicine are not a replacement for counselling or psychotherapy, but they can sit alongside those supports and help your body and nervous system integrate the work you are doing elsewhere.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Is This Approach Right for You? Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Is This Approach Right for You?
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Long-term women's health care at Raintree may be a good fit if you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Prefer to build a relationship with one practitioner over time</li>
                <li>Have health patterns that evolve rather than neatly "resolve" in a few sessions</li>
                <li>Value a gentle, steady approach that respects both your medical care and your lived experience</li>
                <li>Want a place you can return to during different chapters of your life, knowing you will be recognised and remembered</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                If you're unsure, you don't need to commit to long-term care from the outset. You can start with a single Initial Consultation & Treatment and see how it feels.
              </p>
              <p className="text-gray-600 text-left">
                You're also welcome to book a{' '}
                <button
                  onClick={() => openBooking('longterm_approach_free_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  free 15-minute phone consult
                </button>
                {' '}to talk briefly with Renee about what you're looking for and whether this style of care is likely to be helpful.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Appointments, Locations & How to Start */}
      <SimpleLocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `longterm_locations_${locationName.toLowerCase()}` })
        openBooking(`longterm_locations_${locationName.toLowerCase()}`)
      }} />

      {/* Next Steps CTA Section */}
      <section className="py-16 lg:py-32 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h2 className="text-2xl md:text-xl mb-10 text-[#3f4141]">
                Next steps
              </h2>
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  For new long-term care patients, the best starting point is:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 inline-block text-left max-w-lg mx-auto">
                  <li>
                    <strong>Initial Consultation & Treatment</strong> – a longer first appointment to hear your story, understand your current priorities and begin treatment.
                  </li>
                </ul>
                <p className="text-gray-600 max-w-lg mx-auto">
                  From there, you and Renee can decide together on an appointment rhythm that feels appropriate and sustainable.
                </p>
              </div>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => {
                    trackEvent('book_appointment_click', { location: 'longterm_cta_view_appointments' })
                    navigate('/appointments')
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => openBooking('longterm_cta_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-gray-600 text-gray-600 hover:bg-gray-50 sm:w-1/2 mx-auto"
                onClick={() => openBooking('longterm_cta_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Title centered above two columns */}
            <div className="hidden lg:block">
              <h2 className="text-3xl lg:text-4xl mb-16 text-[#3f4141] text-center">
                Next steps
              </h2>
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
                <div>
                  <p className="text-gray-600 mb-4 text-base lg:text-md">
                    For new long-term care patients, the best starting point is:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-base lg:text-md">
                    <li>
                      <strong>Initial Consultation & Treatment</strong> – a longer first appointment to hear your story, understand your current priorities and begin treatment.
                    </li>
                  </ul>
                  <p className="text-gray-600 text-base lg:text-md">
                    From there, you and Renee can decide together on an appointment rhythm that feels appropriate and sustainable.
                  </p>
                </div>
                <div className="flex flex-col gap-3 mx-auto px-4 w-80">
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => {
                      trackEvent('book_appointment_click', { location: 'longterm_cta_view_appointments' })
                      navigate('/appointments')
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      Appointment Types & Fees
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Button>
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('longterm_cta_initial_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Initial Consult & Treatment
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-gray-600 text-gray-600 hover:bg-gray-50"
                    onClick={() => openBooking('longterm_cta_free_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Free 15-minute phone consult
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  )
}

